/**
 * Purpose: write the first agent chat message of a project made from an
 *          FFU: what was created, the project at a glance, its
 *          requirements, and last where the import had to choose, so the
 *          user knows what the project holds and what to review first.
 *
 * Input:   {@link FfuBaselineSummary}: the project as created, building by
 *          building and storey by storey; notes built by code: the opening
 *          line, overview lines, requirement lines, English choice lines
 *          with their reasons and the documents' notes on buildings
 *          modelled as one (Swedish); plus the merge step's value conflicts
 *          (field path, value used, other values read; values may be
 *          Swedish) and building names. The model writes the message from
 *          these; the notes are material, not lines to copy.
 * Output:  `ffu_chat_message` — the whole message as Markdown.
 * Consumer: `server/src/services/project-file-batches/ffu-chat-message.ts`;
 *          a human reads it as the project's first agent chat message.
 *
 * Schema is the source of truth for the output shape.
 */

import { groupBy, uniq, uniqBy } from 'lodash';
import { z } from 'zod';
import type { ExtractedDesignFactConflict } from '../models/extracted-design-facts.model';
import type {
  FfuBuilding,
  FfuOutputFacts,
  FfuStorey,
} from '../models/ffu-output.model';
import type {
  ProjectFileBatchBuildingComposition,
  ProjectFileBatchConsolidatedBuilding,
} from '../models/project-file-batch.model';
import {
  FFU_MESSAGE_HEADINGS,
  ffuNumber,
  shortBuildingName,
  type FfuBaselineSummary,
} from './ffu-baseline-summary.helpers';
import { parseCanonicalStoreyToken } from './extracted-storey-key.helpers';
import { assertUnreachable } from './function.helpers';

export const FFU_CHAT_MESSAGE_SCHEMA_NAME = 'ffu_chat_message' as const;

export const ffuChatMessageAiSchema = z.object({
  message: z
    .string()
    .describe(
      'The whole chat message in Markdown: the opening line, then each section that has content as a "### " heading with "- " bullets.',
    ),
});
export type FfuChatMessageAi = z.infer<typeof ffuChatMessageAiSchema>;

export const buildFfuChatMessageJsonSchema = (): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(
    ffuChatMessageAiSchema,
  ) as Record<string, unknown> & { $schema?: string };
  return rest;
};

const TASK = [
  'A Nodon project was just created from the documents of a construction tender (FFU). Write its first chat message: what the project holds, the requirements the documents set, and the assumptions and decisions the import made where the documents were unclear, so the user knows what to review first.',
  '',
  '"Project as created" is the project as it now stands. The notes after it were written by code from the same data and the import\'s checks. They hold what to tell, but their wording can be clumsy, repeat itself or not make sense. Use them as material and check them against the project; then write the message yourself. Join what belongs together, leave out what repeats or says nothing, and never pass on a note that does not make sense.',
  '',
  `Layout: an opening line, then these sections as "### " headings with "- " bullets: ${FFU_MESSAGE_HEADINGS.overview}, ${FFU_MESSAGE_HEADINGS.requirements}, ${FFU_MESSAGE_HEADINGS.choices}. Leave out a section with nothing to say. Bold only a short subject at the start of a bullet, such as **GFA:**.`,
  '',
  'Be terse. Give numbers and units as the input gives them, never rounded or invented. Every choice says why, with a reason the input gives: an estimate is not a reading, and a missing value is not a disagreement.',
  '',
  'Under "Conflicts" the documents disagree: `used` is the value the project got and `also read` the other values; a conflict with only `read` gives after it what the project holds instead. A path names the building key and the field; "Buildings" gives the name of each key. Fields: floorToFloorHeightM is the storey height, areaM2 the storey area, btaM2 the GFA, ljusBtaM2 the GFA above ground, boaM2 the residential area, loaM2 the premises area, windowRatio the window-to-wall ratio. Give a disagreement as one range per building, not per storey.',
  '',
  'The "Composition notes" are the documents\' own words on why buildings were modelled as one; say why in a short sentence of your own, never quote them.',
  '',
  'Write English only. Translate Swedish document terms and requirement labels ("Högsta byggnadshöjd" is "Maximum building height"). Number storeys as "Project as created" does. Keep names as printed: building names as the project names them, never by key, and product and material names. Do not mention conflicts, paths, keys, notes, templates or the pipeline.',
].join('\n');

/** The name a conflict path's building key stands for. */
export type FfuBuildingName = { key: string; name: string };

/**
 * Every building key a conflict path can hold, with its name: the final
 * buildings' ids and folded keys, then the consolidated buildings.
 */
export const ffuBuildingNames = ({
  buildings,
  consolidatedBuildings,
}: {
  buildings: FfuOutputFacts['buildings'];
  consolidatedBuildings: readonly ProjectFileBatchConsolidatedBuilding[];
}): FfuBuildingName[] => {
  const final = buildings.flatMap(({ id, instanceKeys, buildingName }) => {
    if (!buildingName) return [];
    const keys = id ? [id, ...instanceKeys] : instanceKeys;
    return keys.map((key) => ({ key, name: shortBuildingName(buildingName) }));
  });
  const consolidated = consolidatedBuildings.flatMap(
    ({ buildingInstanceKey, buildingLabel }) =>
      buildingInstanceKey && buildingLabel
        ? [{ key: buildingInstanceKey, name: shortBuildingName(buildingLabel) }]
        : [],
  );
  return uniqBy([...final, ...consolidated], 'key');
};

/** Name disagreements: the message already uses one name, and a user has nothing to review. */
const isNameConflict = ({ path }: ExtractedDesignFactConflict): boolean =>
  path === 'projectName' || path.endsWith('.buildingName');

/**
 * A total printed for several buildings. Its path is `buildings[_]`, so two
 * totals for different groups look like one disagreement, and the line cannot
 * name the buildings. The overview already gives each building's area.
 */
const isCoveredTotalConflict = ({
  path,
}: ExtractedDesignFactConflict): boolean => path.startsWith('buildings[_].');

/** The conflicts on a field a user can review. */
const reviewableConflicts = (
  conflicts: readonly ExtractedDesignFactConflict[],
): ExtractedDesignFactConflict[] =>
  conflicts.filter(
    (conflict) =>
      !isNameConflict(conflict) && !isCoveredTotalConflict(conflict),
  );

/** `buildings[key].field` or `buildings[key].storeys[token].field`. */
const CONFLICT_PATH =
  /^buildings\[([^\]]+)\]\.(?:storeys\[([^\]]+)\]\.)?([A-Za-z0-9.]+)$/;

/** What the project holds where the documents disagree. */
export type FfuConflictUse =
  | { kind: 'value'; value: number }
  | { kind: 'template_default' }
  | { kind: 'drawn_outlines' }
  | { kind: 'joined_building' }
  | { kind: 'not_set' };

const CONFLICT_FIELD_NAMES = [
  'btaM2',
  'ljusBtaM2',
  'boaM2',
  'loaM2',
  'windowRatio',
  'residential.bathroomAreaPerApartmentM2',
  'stairwells.apartmentsPerStairwellPerStorey',
  'floorToFloorHeightM',
  'areaM2',
] as const;

type ConflictFieldName = (typeof CONFLICT_FIELD_NAMES)[number];

const isConflictFieldName = (field: string): field is ConflictFieldName =>
  (CONFLICT_FIELD_NAMES as readonly string[]).includes(field);

/** A conflict worth reviewing, with what the project got. */
export type FfuProjectConflict = {
  path: string;
  /** Building key the path names. */
  key: string;
  field: ConflictFieldName;
  /** Every value the documents gave. */
  read: unknown[];
  use: FfuConflictUse;
};

/** A conflict's field as the message names and formats it. */
type ConflictFormat = {
  label: string;
  number: (value: number) => string;
  unit: string;
};

/**
 * Where the project's value for a field comes from. The project holds the
 * first building's window ratio, and its areas are its drawn outlines.
 */
type ConflictField = ConflictFormat &
  (
    | {
        scope: 'building';
        use: (
          building: FfuBuilding,
          windowRatio: number | null,
        ) => FfuConflictUse;
      }
    | { scope: 'storey'; use: (storey: FfuStorey) => FfuConflictUse }
  );

const valueOr = (
  value: number | null | undefined,
  missing: FfuConflictUse,
): FfuConflictUse => (value == null ? missing : { kind: 'value', value });

const NOT_SET: FfuConflictUse = { kind: 'not_set' };
const TEMPLATE_DEFAULT: FfuConflictUse = { kind: 'template_default' };
const DRAWN_OUTLINES: FfuConflictUse = { kind: 'drawn_outlines' };
const JOINED_BUILDING: FfuConflictUse = { kind: 'joined_building' };

const m2Format = { number: ffuNumber, unit: ' m²' };

const CONFLICT_FIELDS: Record<ConflictFieldName, ConflictField> = {
  btaM2: {
    label: 'GFA',
    ...m2Format,
    scope: 'building',
    use: () => DRAWN_OUTLINES,
  },
  ljusBtaM2: {
    label: 'GFA above ground',
    ...m2Format,
    scope: 'building',
    use: (building) =>
      valueOr(building.geometry.areas.gfa_above_ground, NOT_SET),
  },
  boaM2: {
    label: 'Residential area',
    ...m2Format,
    scope: 'building',
    use: (building) => valueOr(building.geometry.areas.livingAreaM2, NOT_SET),
  },
  loaM2: {
    label: 'Premises area',
    ...m2Format,
    scope: 'building',
    use: (building) => valueOr(building.geometry.areas.premisesAreaM2, NOT_SET),
  },
  windowRatio: {
    label: 'Window-to-wall ratio',
    number: (value) => ffuNumber(Math.round(value * 100)),
    unit: ' %',
    scope: 'building',
    use: (_building, windowRatio) => valueOr(windowRatio, TEMPLATE_DEFAULT),
  },
  'residential.bathroomAreaPerApartmentM2': {
    label: 'Bathroom area per apartment',
    ...m2Format,
    scope: 'building',
    use: (building) =>
      valueOr(building.residential?.bathroomAreaPerApartmentM2, NOT_SET),
  },
  'stairwells.apartmentsPerStairwellPerStorey': {
    label: 'Apartments per stairwell and storey',
    number: ffuNumber,
    unit: '',
    scope: 'building',
    use: (building) =>
      valueOr(building.stairwells?.apartmentsPerStairwellPerStorey, NOT_SET),
  },
  floorToFloorHeightM: {
    label: 'Storey height',
    number: ffuNumber,
    unit: ' m',
    scope: 'storey',
    use: (storey) => valueOr(storey.height, TEMPLATE_DEFAULT),
  },
  areaM2: {
    label: 'Storey area',
    ...m2Format,
    scope: 'storey',
    use: () => DRAWN_OUTLINES,
  },
};

const storeyForToken = (
  building: FfuBuilding,
  token: string,
): FfuStorey | undefined => {
  const key = parseCanonicalStoreyToken(token);
  if (key === null) return undefined;
  const { index, planNumbers } = key;
  if (index !== null)
    return building.geometry.storeys.find((storey) => storey.index === index);
  const [plan] = planNumbers;
  if (plan === undefined) return undefined;
  return building.geometry.storeys.find((storey) =>
    storey.planNumbers.includes(plan),
  );
};

/**
 * The project building a conflict's key names: by its own key or an alias,
 * or as a house a composition joined into it.
 */
type ProjectBuilding = {
  building: FfuBuilding;
  via: 'own_key' | 'joined_house';
};

const buildingWithKey = (
  buildings: readonly FfuBuilding[],
  key: string,
): FfuBuilding | undefined =>
  buildings.find((row) => row.id === key || row.instanceKeys.includes(key));

const projectBuildingOf = (
  buildings: readonly FfuBuilding[],
  compositions: readonly ProjectFileBatchBuildingComposition[],
  key: string,
): ProjectBuilding | null => {
  const own = buildingWithKey(buildings, key);
  if (own !== undefined) return { building: own, via: 'own_key' };
  const joined = compositions
    .filter(
      (row) =>
        row.shouldModelAsSingleBuilding &&
        row.composingBuildingKeys.includes(key),
    )
    .flatMap((row) => row.composingBuildingKeys)
    .map((houseKey) => buildingWithKey(buildings, houseKey))
    .find((building) => building !== undefined);
  return joined === undefined
    ? null
    : { building: joined, via: 'joined_house' };
};

/**
 * A joined house's own building value is not one the project holds: it holds
 * one value for the buildings it joins.
 */
const conflictUse = (
  field: ConflictField,
  { building, via }: ProjectBuilding,
  storeyToken: string | undefined,
  windowRatio: number | null,
): FfuConflictUse | null => {
  switch (field.scope) {
    case 'building': {
      if (storeyToken !== undefined) return null;
      const use = field.use(building, windowRatio);
      return via === 'joined_house' && use.kind === 'value'
        ? JOINED_BUILDING
        : use;
    }
    case 'storey': {
      if (storeyToken === undefined) return null;
      const storey = storeyForToken(building, storeyToken);
      return storey === undefined ? null : field.use(storey);
    }
    default:
      return assertUnreachable(field);
  }
};

/**
 * Conflict values as the message gives them: numbers as one value or a
 * range with their unit ("1,271–7,118 m²", "30–40 %"), other values listed.
 */
const conflictValues = (
  field: ConflictFieldName,
  values: readonly unknown[],
): string => {
  const { number, unit } = CONFLICT_FIELDS[field];
  const numbers = values.filter(
    (value): value is number => typeof value === 'number',
  );
  if (numbers.length < values.length)
    return uniq(values.map(String)).join(', ');
  const low = number(Math.min(...numbers));
  const high = number(Math.max(...numbers));
  return low === high ? `${low}${unit}` : `${low}–${high}${unit}`;
};

const USE_REASONS: Record<Exclude<FfuConflictUse['kind'], 'value'>, string> = {
  template_default: "the project keeps the template's value",
  drawn_outlines: "the project's area is the area of its drawn outlines",
  joined_building: 'the project holds one value for the buildings it joins',
  not_set: 'the project holds no value',
};

/** The values read that differ from the one the project holds. */
const otherValues = (
  field: ConflictFieldName,
  read: readonly unknown[],
  used: number,
): unknown[] => {
  const shown = conflictValues(field, [used]);
  return read.filter((value) => conflictValues(field, [value]) !== shown);
};

/** A conflict whose every read value is the one the project holds is no choice. */
const isOpenConflict = ({ field, read, use }: FfuProjectConflict): boolean =>
  use.kind !== 'value' || otherValues(field, read, use.value).length > 0;

/**
 * The conflicts that are a choice to review, each with what the project
 * holds: the value of its final building or storey, the template default
 * where none was set, the drawn outlines for an area, or the joined
 * building's value for a house joined into it. A conflict on a field the
 * project does not hold, on a part, on a building or storey the project no
 * longer has, or whose every value is the one the project holds is left out.
 */
export const ffuProjectConflicts = (
  conflicts: readonly ExtractedDesignFactConflict[],
  {
    facts,
    compositions,
  }: {
    facts: FfuOutputFacts;
    compositions: readonly ProjectFileBatchBuildingComposition[];
  },
): FfuProjectConflict[] => {
  const windowRatio =
    facts.buildings[0]?.properties.window_ratio?.value ?? null;
  return reviewableConflicts(conflicts)
    .flatMap((conflict): FfuProjectConflict[] => {
      const [, key, storeyToken, fieldName] =
        CONFLICT_PATH.exec(conflict.path) ?? [];
      if (key === undefined || fieldName === undefined) return [];
      if (!isConflictFieldName(fieldName)) return [];
      const target = projectBuildingOf(facts.buildings, compositions, key);
      if (target === null) return [];
      const use = conflictUse(
        CONFLICT_FIELDS[fieldName],
        target,
        storeyToken,
        windowRatio,
      );
      if (use === null) return [];
      const read = [
        ...(conflict.picked === null ? [] : [conflict.picked]),
        ...conflict.rejected.map((row) => row.value),
      ];
      return [{ path: conflict.path, key, field: fieldName, read, use }];
    })
    .filter(isOpenConflict);
};

const conflictLine = ({
  path,
  field,
  read,
  use,
}: FfuProjectConflict): string =>
  use.kind === 'value'
    ? `- ${path}: used ${conflictValues(field, [use.value])}; also read ${conflictValues(field, otherValues(field, read, use.value))}`
    : `- ${path}: read ${conflictValues(field, read)}; ${USE_REASONS[use.kind]}`;

const storeysText = (count: number): string =>
  count === 1 ? 'one storey' : `${count} storeys`;

/**
 * What one subject's line says the project holds. Storeys that got a value
 * and storeys that did not are named apart, so the line does not give both
 * as one choice.
 */
const choiceText = (
  field: ConflictFieldName,
  used: readonly number[],
  reasons: readonly string[],
): string => {
  const usedText = `used ${conflictValues(field, used)}`;
  if (reasons.length === 0) return usedText;
  if (used.length === 0) return reasons.join('; ');
  if (CONFLICT_FIELDS[field].scope === 'storey') {
    return `${usedText} on ${storeysText(used.length)}; on the others, ${reasons.join('; ')}`;
  }
  return [usedText, ...reasons].join('; ');
};

/**
 * The conflicts as choice lines, one per building and field, for the
 * message code writes when the model's fails: "**GFA (Hus A):** the
 * documents disagree: 100–120 m²; the project's area is the area of its
 * drawn outlines."
 */
export const ffuConflictChoiceLines = (
  conflicts: readonly FfuProjectConflict[],
  buildingNames: readonly FfuBuildingName[],
): string[] => {
  const subjects = conflicts.map((conflict) => {
    const name = buildingNames.find((row) => row.key === conflict.key)?.name;
    const { label } = CONFLICT_FIELDS[conflict.field];
    return { conflict, subject: name ? `${label} (${name})` : label };
  });
  return Object.values(groupBy(subjects, 'subject')).flatMap((group) => {
    const [first] = group;
    if (!first) return [];
    const { field } = first.conflict;
    const read = group.flatMap(({ conflict }) => conflict.read);
    const used = group.flatMap(({ conflict }) =>
      conflict.use.kind === 'value' ? [conflict.use.value] : [],
    );
    const reasons = uniq(
      group.flatMap(({ conflict }) =>
        conflict.use.kind === 'value' ? [] : [USE_REASONS[conflict.use.kind]],
      ),
    );
    return [
      `**${first.subject}:** the documents disagree: ${conflictValues(field, read)}; ${choiceText(field, used, reasons)}.`,
    ];
  });
};

const factsBlock = (title: string, lines: readonly string[]): string[] =>
  lines.length === 0
    ? []
    : ['', `${title}:`, ...lines.map((line) => `- ${line}`)];

/** The user prompt for one import's message. */
export const buildFfuChatMessagePrompt = ({
  summary,
  conflicts,
  buildingNames,
}: {
  summary: FfuBaselineSummary;
  conflicts: readonly FfuProjectConflict[];
  buildingNames: readonly FfuBuildingName[];
}): string => {
  return [
    TASK,
    '',
    'Project as created:',
    ...summary.project,
    '',
    'Opening:',
    summary.opening,
    ...factsBlock('Overview', summary.overview),
    ...factsBlock('Requirements', summary.requirements),
    ...factsBlock('Choices', summary.choices),
    ...factsBlock('Composition notes', summary.compositionNotes),
    ...(conflicts.length > 0
      ? ['', 'Conflicts:', ...conflicts.map(conflictLine)]
      : []),
    ...(conflicts.length > 0 && buildingNames.length > 0
      ? [
          '',
          'Buildings:',
          ...buildingNames.map(({ key, name }) => `- ${key}: ${name}`),
        ]
      : []),
  ].join('\n');
};
